import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';

const ServiceAreaModal = ({ isOpen, onClose, provider }) => {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Restore focus to whatever triggered this modal once it closes (declared before
  // the focus-management effect below so it captures the trigger element BEFORE
  // that effect moves focus onto the close button).
  useModalFocusReturn(isOpen);

  // Focus management
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus({ preventScroll: true });
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }

      // Tab trap
      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Click outside to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Service areas are the distinct counties this provider serves — each entry in
  // provider.locations (the same array ProviderServiceDetails renders as
  // "Location details", and ProviderSummary reads as provider.locations[0] for the
  // card's primary address) carries its own provPhyCounty. A provider with multiple
  // locations can legitimately span more than one county, so dedupe with a Set
  // rather than assuming they all match; blank/missing county values are dropped
  // rather than rendering an empty list item.
  // When a location also has a name (affiliation/provName — same fields
  // ProviderServiceDetails uses for its "facilityName" header), prefix the county
  // with it as "[Location Name] County" so multiple locations in the same county
  // are still distinguishable, matching the facility-name field already shown per
  // location elsewhere in this component. Dedupe on the final display string
  // (not just the bare county) so this doesn't collapse two different locations'
  // rows into one just because they happen to share a county.
  const serviceAreas = Array.from(
    new Set(
      (provider?.affiliations || [])
        .filter((aff) => aff?.affiliateName && aff.affilType.trim() === 'GSA')
        .map((aff) => {
          const locationName = aff.affiliateName ? aff.affiliateName : 'We can\'t find a service area for this provider. Call them to find out about services in your area.';
          return locationName;
        })
    )
  );

  return ReactDOM.createPortal(
    <div
      className="service-area-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="service-area-modal-title"
    >
      <div className="service-area-modal" ref={modalRef}>
        <button
          className="service-area-modal-close"
          onClick={onClose}
          aria-label="Close modal"
          type="button"
          ref={closeButtonRef}
        >
          ×
        </button>

        <div className="service-area-modal-content">
          <h2 id="service-area-modal-title" className="service-area-modal-title">
            Service area
          </h2>

          <div className="service-area-modal-body">
            {serviceAreas.length > 0 ? (
              <ul className="service-area-list">
                {serviceAreas.map((area, index) => (
                  <li key={index} className="service-area-item">
                    {area}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-service-area">We can't find a service area for this provider. Call them to find out about services in your area.</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ServiceAreaModal;
